import React, { useEffect } from 'react';
import {
  Box, List, ListItem, ListItemIcon, ListItemText, Typography, Chip,
  CircularProgress, Alert, Divider, Stack, Badge,
} from '@mui/material';
import {
  Assignment, Payment, Warning, FlightTakeoff, Hotel, DirectionsCar,
  NotificationsActive,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { loadReminders } from '../../../reduxcomponents/slices/tripManagementSlice';
import dayjs from 'dayjs';

const REMINDER_CONFIG = {
  payment_7d:         { icon: Payment,              color: 'info',    label: 'Payment Due in 7 Days' },
  payment_3d:         { icon: Payment,              color: 'warning', label: 'Payment Due in 3 Days' },
  payment_1d:         { icon: Payment,              color: 'error',   label: 'Payment Due Tomorrow' },
  payment_due:        { icon: Payment,              color: 'error',   label: 'Payment Due Today' },
  payment_overdue:    { icon: Payment,              color: 'error',   label: 'Payment Overdue' },
  trip_start_7d:      { icon: FlightTakeoff,        color: 'info',    label: 'Trip in 7 Days' },
  trip_start_3d:      { icon: FlightTakeoff,        color: 'warning', label: 'Trip in 3 Days' },
  trip_start_1d:      { icon: FlightTakeoff,        color: 'error',   label: 'Trip Tomorrow' },
  hotel_confirmation: { icon: Hotel,                color: 'warning', label: 'Hotel Confirmation' },
  driver_confirmation:{ icon: DirectionsCar,        color: 'warning', label: 'Driver Confirmation' },
  permit:             { icon: Assignment,           color: 'warning', label: 'Permit Required' },
  flight_train:       { icon: FlightTakeoff,        color: 'info',    label: 'Flight/Train Reminder' },
};

const NotificationCenter = () => {
  const dispatch = useDispatch();
  const { reminders, loading } = useSelector(s => s.tripManagement);

  useEffect(() => {
    dispatch(loadReminders());
    const interval = setInterval(() => dispatch(loadReminders()), 120000);
    return () => clearInterval(interval);
  }, [dispatch]);

  const urgent   = reminders.filter(r => ['payment_overdue', 'payment_due', 'trip_start_1d'].includes(r.reminder_type));
  const upcoming = reminders.filter(r => !urgent.includes(r));

  if (loading.reminders && !reminders.length) {
    return <Box display="flex" justifyContent="center" py={3}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} mb={2}>
        <Badge badgeContent={reminders.length} color="error" max={99}>
          <NotificationsActive color="action" />
        </Badge>
        <Typography variant="subtitle2" fontWeight={700}>Reminders & Notifications</Typography>
      </Stack>

      {reminders.length === 0 && (
        <Alert severity="success">All caught up! No pending reminders.</Alert>
      )}

      {urgent.length > 0 && (
        <Box mb={2}>
          <Typography variant="caption" fontWeight={700} color="error.main" textTransform="uppercase" letterSpacing={0.5}>
            🔴 Urgent ({urgent.length})
          </Typography>
          <List dense disablePadding>
            {urgent.map((r, i) => {
              const cfg = REMINDER_CONFIG[r.reminder_type] || {};
              const Icon = cfg.icon || NotificationsActive;
              return (
                <React.Fragment key={r._id}>
                  <ListItem sx={{ px: 0, py: 0.75 }} alignItems="flex-start">
                    <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                      <Icon color={cfg.color} fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                          <Typography variant="body2" fontWeight={600}>{r.title}</Typography>
                          <Chip label={cfg.label} color={cfg.color} size="small" sx={{ height: 18, fontSize: 10 }} />
                        </Stack>
                      }
                      secondary={
                        <Box>
                          <Typography variant="caption" color="text.secondary">{r.message}</Typography>
                          <Typography variant="caption" color="text.disabled" display="block">
                            {r.trip_id?.guest_id?.name} · {r.trip_id?.destination} · Due {dayjs(r.due_date).format('DD MMM')}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                  {i < urgent.length - 1 && <Divider />}
                </React.Fragment>
              );
            })}
          </List>
        </Box>
      )}

      {upcoming.length > 0 && (
        <Box>
          <Typography variant="caption" fontWeight={700} color="text.secondary" textTransform="uppercase" letterSpacing={0.5}>
            📋 Upcoming ({upcoming.length})
          </Typography>
          <List dense disablePadding>
            {upcoming.map((r, i) => {
              const cfg = REMINDER_CONFIG[r.reminder_type] || {};
              const Icon = cfg.icon || NotificationsActive;
              return (
                <React.Fragment key={r._id}>
                  <ListItem sx={{ px: 0, py: 0.75 }} alignItems="flex-start">
                    <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                      <Icon color={cfg.color || 'action'} fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={<Typography variant="body2">{r.title}</Typography>}
                      secondary={
                        <Typography variant="caption" color="text.disabled">
                          {r.trip_id?.guest_id?.name} · Due {dayjs(r.due_date).format('DD MMM YYYY')}
                        </Typography>
                      }
                    />
                  </ListItem>
                  {i < upcoming.length - 1 && <Divider />}
                </React.Fragment>
              );
            })}
          </List>
        </Box>
      )}
    </Box>
  );
};

export default NotificationCenter;
